import { OnboardingStep } from '@repo/shared/dtos/organization'
import { Button } from '@repo/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Language } from '@repo/ui/lang'
import { sortByKey } from '@repo/ui/utils'
import { LogOut, SearchIcon } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { Navigate, useOutletContext } from 'react-router-dom'

import { useAbility } from '@/ability/ability-context'
import { Logo } from '@/components/layouts/Logo'
import { OrganizationCreatorDialog } from '@/components/onboarding/OrganizationCreator'
import { search } from '@/components/search/helpers'
import { useView } from '@/hooks'
import { buildRoute, RouteNames } from '@/routes'
import { ErrorRoute } from '@/routes/ErrorRoute'
import { UserProtectedRouteContext } from '@/routes/types'
import { adminStoreActions, storeActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { Organization } from '@/store/user'

export function LobbyRoute({ organizations }: { organizations: Organization[] }) {
  const { user } = useOutletContext<UserProtectedRouteContext>()

  const view = useView()
  const defaultLanguage = user?.language ?? Language.EN

  // No organizations
  if (organizations.length === 0) {
    return (
      <Layout defaultLanguage={defaultLanguage}>
        <div className="text-lg text-primary-500">
          Please contact the administrator to create an organization.
        </div>
      </Layout>
    )
  }
  // Only one organization
  else if (organizations.length === 1) {
    const organization = organizations[0]
    if (!organization) return <ErrorRoute message="Organization not found" />
    if (organization.onboarding.step === OnboardingStep.COMPLETED) {
      return <Navigate to={view.dashboard(organization.id).route} />
    }
    return (
      <Navigate
        to={buildRoute(RouteNames.Organizations, [organization.id, RouteNames.Onboarding])}
      />
    )
  }

  return (
    <Layout defaultLanguage={defaultLanguage}>
      <OrganizationList organizations={organizations} view={view} />
    </Layout>
  )
}

function Layout({ children, defaultLanguage }: { children: ReactNode; defaultLanguage: Language }) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()
  const logout = () => dispatch(storeActions.logout())
  const ability = useAbility()
  return (
    <div className="relative flex max-h-screen flex-1 flex-col overflow-hidden overflow-y-auto">
      <div className="bg-primary-50 flex items-center justify-center">
        <div className="text-primary my-6 flex items-center justify-center gap-4">
          <Logo isWhite={false} />
        </div>
      </div>
      <div className="my-20 flex flex-col items-center justify-center px-6">
        {children}

        {ability.can('create', 'Organization') && (
          <OrganizationCreatorDialog
            defaultLanguage={defaultLanguage}
            onSubmit={(v) => {
              void dispatch(adminStoreActions.onboarding.createOrganization(v))
            }}
            className="w-full lg:w-1/2 2xl:w-1/3 mt-4"
          />
        )}

        <Button
          variant="link"
          className="mt-2 opacity-70 transition-opacity hover:opacity-100"
          onClick={() => void logout()}
        >
          <LogOut className="size-4" />
          {tc('logout')}
        </Button>
      </div>
    </div>
  )
}

function OrganizationList({
  organizations,
  view,
}: {
  organizations: Organization[]
  view: ReturnType<typeof useView>
}) {
  const { tc } = useT()
  const sorted = useMemo(() => [...organizations].sort(sortByKey('name')), [organizations])
  const [query, setQuery] = useState('')
  const searchResults = useMemo(() => {
    return search(sorted, query, ['name'])
  }, [query, sorted])
  return (
    <>
      <Card className="flex w-full flex-col lg:w-1/2 2xl:w-1/3" variant="passive">
        <CardHeader>
          <CardTitle>
            {tc('organizations', {
              cfl: true,
              count: organizations.length,
            })}{' '}
          </CardTitle>
          <CardDescription>{tc('result', { count: searchResults.length })}</CardDescription>
          <CardAction>
            <div className="relative">
              <SearchIcon className="text-primary-400 absolute left-3 top-1/2 size-4 -translate-y-1/2 transform" />
              <Input
                type="search"
                value={query}
                autoFocus
                className="pl-9"
                onChange={(e) => setQuery(e.target.value)}
                placeholder={tc('search', { cfl: true }) + '...'}
              />
            </div>
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-4">
          <div className="flex flex-1 flex-col gap-2">
            {searchResults.map(({ item: org }) => (
              <Button
                key={org.id}
                onClick={view.dashboard(org.id).navigate}
                className="justify-start"
                variant="outline"
              >
                {org.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  )
}
